import React, { useState, useEffect } from 'react';
import { samRumahGroupAPI, userAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Settings, RefreshCw, Shield } from 'lucide-react';

const AdminSamRumahGroup = () => {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [memberRole, setMemberRole] = useState('handler');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const groupRes = await samRumahGroupAPI.getGroup();
      setGroup(groupRes.data);
      
      // Load users for adding members
      const usersRes = await userAPI.getUsers({ role: 'agent' });
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      // If group doesn't exist, initialize it
      if (error.response?.status === 404) {
        try {
          await samRumahGroupAPI.init();
          const groupRes = await samRumahGroupAPI.getGroup();
          setGroup(groupRes.data);
        } catch (initError) {
          toast.error('Failed to initialize SAM Rumah Group');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    try {
      await samRumahGroupAPI.addMember(selectedUserId, memberRole);
      toast.success('Member added successfully');
      setShowAddModal(false);
      setSelectedUserId('');
      setMemberRole('handler');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      await samRumahGroupAPI.removeMember(userId);
      toast.success('Member removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleUpdateSettings = async (field, value) => {
    try {
      await samRumahGroupAPI.updateGroup({ [field]: value });
      toast.success('Settings updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Filter users not already in group
  const availableUsers = users.filter(
    user => !group?.members?.some(m => m.user_id === user.id)
  );

  const filteredUsers = availableUsers.filter(user =>
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sam-rumah-group-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SAM Rumah Group</h1>
          <p className="text-gray-500">Manage the default handler group for customer appointments</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} data-testid="add-member-btn">
          <UserPlus className="h-4 w-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Group Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Group Settings
          </CardTitle>
          <CardDescription>
            Configure how appointments are routed to group members
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input 
                value={group?.name || ''} 
                onChange={(e) => handleUpdateSettings('name', e.target.value)}
                data-testid="group-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input 
                value={group?.description || ''} 
                onChange={(e) => handleUpdateSettings('description', e.target.value)}
                data-testid="group-description-input"
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <p className="font-medium">Auto-Assign (Round Robin)</p>
              <p className="text-sm text-gray-500">
                Automatically assign appointments to members in rotation
              </p>
            </div>
            <Switch 
              checked={group?.auto_assign || false}
              onCheckedChange={(checked) => handleUpdateSettings('auto_assign', checked)}
              data-testid="auto-assign-switch"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <p className="font-medium">Group Active</p>
              <p className="text-sm text-gray-500">
                Enable or disable the group for new appointments
              </p>
            </div>
            <Switch 
              checked={group?.is_active || false}
              onCheckedChange={(checked) => handleUpdateSettings('is_active', checked)}
              data-testid="group-active-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Members Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Group Members ({group?.members?.length || 0})
          </CardTitle>
          <CardDescription>
            These handlers will receive appointment requests from customers without agent uplines
          </CardDescription>
        </CardHeader>
        <CardContent>
          {group?.members?.length === 0 ? (
            <div className="text-center py-8 text-gray-500" data-testid="no-members">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No members in this group yet</p>
              <p className="text-sm">Add members to start receiving appointment requests</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {group?.members?.map((member) => (
                  <TableRow key={member.user_id} data-testid={`member-row-${member.user_id}`}>
                    <TableCell className="font-medium">
                      {member.user?.full_name || 'Unknown'}
                    </TableCell>
                    <TableCell>{member.user?.email || '-'}</TableCell>
                    <TableCell>{member.user?.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
                        {member.role === 'admin' ? (
                          <><Shield className="h-3 w-3 mr-1" /> Admin</>
                        ) : (
                          'Handler'
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {member.added_at ? new Date(member.added_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-member-${member.user_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Member Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent data-testid="add-member-modal">
          <DialogHeader>
            <DialogTitle>Add Member to SAM Rumah Group</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Search Users</Label>
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-users-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Select User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger data-testid="select-user-trigger">
                  <SelectValue placeholder="Choose a user to add" />
                </SelectTrigger>
                <SelectContent>
                  {filteredUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} - {user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Member Role</Label>
              <Select value={memberRole} onValueChange={setMemberRole}>
                <SelectTrigger data-testid="select-role-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="handler">Handler</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMember} data-testid="confirm-add-member-btn">
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSamRumahGroup;
